import React from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { formatDateAsLocaleDateString } from "utils/formatters/DateFormatters";
import type { Subscription, DeactivationRecord } from "client/api-v2";
import { useEntity } from "components/EntityProvider/EntityProvider";

function parseDate(value: string): DateTime {
  return DateTime.fromISO(value, { zone: "utc" });
}

function formatDuration(deactivatedAt: DateTime, reactivatedAt: DateTime | null): string {
  const end = reactivatedAt ?? DateTime.utc();
  const diff = end.diff(deactivatedAt, "days");
  const days = Math.floor(diff.days);
  if (days === 0) return "< 1 day";
  if (days === 1) return "1 day";
  return `${days} days`;
}

export function SubscriptionDeactivationHistory() {
  const { entity: subscription } = useEntity<Subscription>();

  const records = subscription?.deactivationHistory;

  if (!records || records.length === 0) {
    return <Typography color="textSecondary">No deactivation history.</Typography>;
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Deactivation History
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Deactivated</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Reactivated</TableCell>
              <TableCell>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record: DeactivationRecord, index: number) => {
              const deactivatedAt = parseDate(record.deactivatedAt);
              const reactivatedAt = record.reactivatedAt ? parseDate(record.reactivatedAt) : null;

              return (
                <TableRow key={index}>
                  <TableCell>{formatDateAsLocaleDateString(deactivatedAt)}</TableCell>
                  <TableCell>{record.reason}</TableCell>
                  <TableCell>
                    {reactivatedAt ? formatDateAsLocaleDateString(reactivatedAt) : "Currently deactivated"}
                  </TableCell>
                  <TableCell>{formatDuration(deactivatedAt, reactivatedAt)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
